import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Platform } from 'react-native';
import { useSelector } from 'react-redux';
import * as FileSystem from 'expo-file-system/legacy';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useAppDispatch } from '../store';
import type { RootState } from '../store';
import { uploadDocument } from '../store/actions/notes';
import { showNotification } from '../store/actions/auth';
import { navigationRef } from '../navigation/navigationRef';
import { hapticCreationPulse } from '../utilities/haptics';

type ProjectLike = {
  id: number | string;
  updated_at?: string;
  project_name?: string;
  first_name?: string;
};

type ShareIntentState = {
  isReady: boolean;
  hasShareIntent: boolean;
  shareIntent: any;
  resetShareIntent: (clearNativeModule?: boolean) => void;
};
const SHARE_IMPORT_TOOLTIP_SEEN_KEY = '@eleplan/share_import_tooltip_seen';

let useShareIntentContextSafe: () => ShareIntentState = () => ({
  isReady: false,
  hasShareIntent: false,
  shareIntent: null,
  resetShareIntent: () => {},
});
try {
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const mod = require('expo-share-intent');
  if (typeof mod?.useShareIntentContext === 'function') {
    useShareIntentContextSafe = mod.useShareIntentContext;
  }
} catch {
  // Optional native module.
}

function buildFingerprint(payload: any): string {
  const files = Array.isArray(payload?.files) ? payload.files : [];
  const fileSig = files
    .map((f: any) => `${f?.fileName || 'file'}:${f?.size || 0}:${f?.path || ''}`)
    .join('|');
  return [
    payload?.type || '',
    payload?.text || '',
    payload?.webUrl || '',
    fileSig,
  ].join('::');
}

function sanitizeFilename(name: string): string {
  return String(name || 'shared-item').replace(/[\\/:*?"<>|]/g, '-');
}

function projectLabel(project: ProjectLike): string {
  return project.project_name || project.first_name || `Eleplan ${project.id}`;
}

async function createTextShareFile(payload: any): Promise<{ uri: string; fileName: string; mimeType: string; size: number } | null> {
  const text = String(payload?.text || '').trim();
  const webUrl = String(payload?.webUrl || '').trim();
  const title = String(payload?.meta?.title || '').trim();

  const chunks: string[] = [];
  if (title) chunks.push(`# ${title}`);
  if (webUrl) chunks.push(`Source URL: ${webUrl}`);
  if (text) chunks.push(text);
  const content = chunks.join('\n\n').trim();
  if (!content) return null;

  const baseDir = FileSystem.cacheDirectory || FileSystem.documentDirectory;
  if (!baseDir) return null;
  const fileName = `shared-email-${Date.now()}.txt`;
  const uri = `${baseDir}${fileName}`;
  await FileSystem.writeAsStringAsync(uri, content, { encoding: FileSystem.EncodingType.UTF8 });
  return {
    uri,
    fileName,
    mimeType: 'text/plain',
    size: content.length,
  };
}

export default function ShareIntentIngestionBridge() {
  const dispatch = useAppDispatch();
  const { hasShareIntent, shareIntent, resetShareIntent, isReady } = useShareIntentContextSafe();
  const authorized = useSelector((s: RootState) => s.session.authorized);
  const accountId = useSelector((s: RootState) => (s as any).session?.account?.account_id);
  const projectsMap = useSelector((s: RootState) => (s.projects?.list || {}) as Record<string, ProjectLike>);

  const projects = useMemo(() => Object.values(projectsMap), [projectsMap]);
  const lastProcessedFingerprintRef = useRef<string | null>(null);
  const inFlightRef = useRef(false);
  const promptingRef = useRef(false);

  const importToProject = useCallback(async (targetProject: ProjectLike, payload: any, fingerprint: string) => {
    inFlightRef.current = true;
    try {
      const files = Array.isArray(payload?.files) ? payload.files : [];
      let successCount = 0;

      for (const file of files) {
        if (!file?.path) continue;
        const mimeType = file.mimeType || 'application/octet-stream';
        const fileName = sanitizeFilename(file.fileName || `shared-file-${Date.now()}`);
        const result = await dispatch(uploadDocument(
          targetProject.id,
          {
            filename: fileName,
            name: fileName,
            kb_access: true,
            size: file.size || undefined,
            type: mimeType,
            mime: mimeType,
            project_id: targetProject.id,
          },
          file.path,
          mimeType,
        ) as any);
        if (result?.success) successCount++;
      }

      // Mail shares frequently arrive as text/web URL only.
      const textFile = await createTextShareFile(payload);
      if (textFile) {
        const result = await dispatch(uploadDocument(
          targetProject.id,
          {
            filename: textFile.fileName,
            name: textFile.fileName,
            kb_access: true,
            size: textFile.size,
            type: textFile.mimeType,
            mime: textFile.mimeType,
            project_id: targetProject.id,
          },
          textFile.uri,
          textFile.mimeType,
        ) as any);
        if (result?.success) successCount++;
      }

      if (successCount > 0) {
        hapticCreationPulse();
        dispatch(showNotification(
          'success',
          successCount === 1 ? 'Shared item imported' : `${successCount} shared items imported`,
          `Imported to ${projectLabel(targetProject)}.`,
        ));
        const seen = await AsyncStorage.getItem(SHARE_IMPORT_TOOLTIP_SEEN_KEY);
        if (!seen) {
          dispatch(showNotification(
            'success',
            'Tip: Import from Mail',
            'In Apple Mail: select email(s) -> Share -> Eleplan.',
          ));
          await AsyncStorage.setItem(SHARE_IMPORT_TOOLTIP_SEEN_KEY, 'true');
        }
        if (navigationRef.isReady()) {
          (navigationRef as any).navigate('Main', {
            screen: 'Plans',
            params: {
              screen: 'ProjectFiles',
              params: { projectId: Number(targetProject.id), initialTab: 'documents' },
            },
          });
        }
      } else {
        dispatch(showNotification('error', 'Import failed', 'No shareable content was found.'));
      }
    } catch {
      dispatch(showNotification('error', 'Import failed', 'Could not import shared content.'));
    } finally {
      resetShareIntent();
      lastProcessedFingerprintRef.current = fingerprint;
      inFlightRef.current = false;
      promptingRef.current = false;
    }
  }, [dispatch, resetShareIntent]);

  useEffect(() => {
    if (Platform.OS !== 'ios') return;
    if (!isReady || !authorized || !accountId || !hasShareIntent || !shareIntent) return;
    if (inFlightRef.current || promptingRef.current) return;

    const fingerprint = buildFingerprint(shareIntent);
    if (!fingerprint || lastProcessedFingerprintRef.current === fingerprint) return;

    if (!projects.length) {
      dispatch(showNotification('error', 'No plan found', 'Open Eleplan and create/select a plan first.'));
      resetShareIntent();
      lastProcessedFingerprintRef.current = fingerprint;
      return;
    }

    promptingRef.current = true;
    const optionLabels = projects.map((p) => projectLabel(p));
    // Access ActionSheetIOS at runtime so web bundling doesn't require this iOS-only export.
    const actionSheetIOS = (require('react-native') as any)?.ActionSheetIOS;
    if (!actionSheetIOS?.showActionSheetWithOptions) {
      dispatch(showNotification('error', 'Import unavailable', 'Project picker is only available on iOS.'));
      resetShareIntent();
      lastProcessedFingerprintRef.current = fingerprint;
      promptingRef.current = false;
      return;
    }
    actionSheetIOS.showActionSheetWithOptions(
      {
        title: 'Choose Eleplan recipient',
        message: 'Where should we import this shared content?',
        options: [...optionLabels, 'Cancel'],
        cancelButtonIndex: optionLabels.length,
      },
      (buttonIndex) => {
        if (buttonIndex === optionLabels.length || buttonIndex < 0) {
          resetShareIntent();
          lastProcessedFingerprintRef.current = fingerprint;
          promptingRef.current = false;
          return;
        }
        const targetProject = projects[buttonIndex];
        if (!targetProject?.id) {
          resetShareIntent();
          lastProcessedFingerprintRef.current = fingerprint;
          promptingRef.current = false;
          return;
        }
        importToProject(targetProject, shareIntent, fingerprint);
      },
    );
  }, [
    isReady,
    authorized,
    accountId,
    hasShareIntent,
    shareIntent,
    projects,
    dispatch,
    resetShareIntent,
    importToProject,
  ]);

  return null;
}
